import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ChevronDown, ChevronRight, Loader2, TrendingUp } from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from 'recharts'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import type { BillingForecastMonth, BillingForecastResponse } from '@toga/shared'

const HORIZONS = [6, 12, 24] as const

export default function ForecastPanel() {
  const { t } = useTranslation('billing')
  const [months, setMonths] = useState<number>(12)
  const [data, setData] = useState<BillingForecastResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get<BillingForecastResponse>(`/billing/forecast?months=${months}`)
      .then((resp) => {
        if (!cancelled) setData(resp)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('forecast.error'))
          setData(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [months, t])

  const toggleMonth = (month: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(month)) next.delete(month)
      else next.add(month)
      return next
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-serif font-semibold t-primary flex items-center gap-2">
            <TrendingUp size={18} className="text-toga-700 dark:text-toga-300" />
            {t('forecast.title')}
          </h2>
          <p className="text-xs t-muted mt-0.5 max-w-2xl">{t('forecast.subtitle')}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs t-muted mr-1">{t('forecast.rangeLabel')}</span>
          {HORIZONS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setMonths(n)}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                months === n
                  ? 'border-toga-500 text-toga-700 dark:text-toga-300 bg-toga-500/10'
                  : 'border-base t-muted hover:bg-hover'
              }`}
            >
              {t(`forecast.months${n}`)}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="surface p-8 text-center t-muted text-sm">
          <Loader2 size={16} className="inline-block animate-spin mr-2" />
          {t('forecast.loading')}
        </div>
      )}

      {!loading && error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {t('forecast.error')}
        </div>
      )}

      {!loading && !error && data && data.totalProjectedCents === 0 && (
        <div className="surface p-10 text-center">
          <TrendingUp size={28} className="mx-auto t-faint mb-2" />
          <p className="t-muted text-sm">{t('forecast.empty')}</p>
        </div>
      )}

      {!loading && !error && data && data.totalProjectedCents !== 0 && (
        <>
          <div className="surface p-4">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.months}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--text-muted, #94a3b8)' }} />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--text-muted, #94a3b8)' }}
                  tickFormatter={(v: number) => formatCents(v)}
                  width={90}
                />
                <Tooltip
                  formatter={(value: number | string) => formatCents(Number(value))}
                  contentStyle={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="projectedCents" fill="#3b82f6" name={t('forecast.colProjected')} />
                <Bar dataKey="netCents" fill="#10b981" name={t('forecast.colNet')} />
                <Bar dataKey="emittedCents" fill="#8b5cf6" name={t('forecast.colEmitted')} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-muted">
                <tr>
                  <th className="px-3 py-2 text-left">{t('forecast.colMonth')}</th>
                  <th className="px-3 py-2 text-right">{t('forecast.colProjected')}</th>
                  <th className="px-3 py-2 text-right">{t('forecast.colCommission')}</th>
                  <th className="px-3 py-2 text-right">{t('forecast.colNet')}</th>
                  <th className="px-3 py-2 text-right">{t('forecast.colEmitted')}</th>
                  <th className="px-3 py-2 text-right">{t('forecast.colCollected')}</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((m) => (
                  <ForecastMonthRows
                    key={m.month}
                    month={m}
                    expanded={expanded.has(m.month)}
                    onToggle={() => toggleMonth(m.month)}
                  />
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-base bg-[var(--bg-input-soft)] font-semibold">
                  <td className="px-3 py-2 t-primary">{t('forecast.total')}</td>
                  <td className="px-3 py-2 text-right font-mono t-primary">
                    {formatCents(data.totalProjectedCents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono t-secondary">
                    {formatCents(data.totalCommissionCents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700 dark:text-emerald-300">
                    {formatCents(data.totalNetCents)}
                  </td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function ForecastMonthRows({
  month,
  expanded,
  onToggle,
}: {
  month: BillingForecastMonth
  expanded: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation('billing')
  const hasItems = month.items.length > 0
  return (
    <>
      <tr
        className={`border-t border-base/40 ${hasItems ? 'cursor-pointer hover:bg-hover' : ''}`}
        onClick={hasItems ? onToggle : undefined}
      >
        <td className="px-3 py-2 t-primary">
          <span className="inline-flex items-center gap-1.5">
            {hasItems ? (
              expanded ? (
                <ChevronDown size={13} className="t-muted" />
              ) : (
                <ChevronRight size={13} className="t-muted" />
              )
            ) : (
              <span className="inline-block w-[13px]" />
            )}
            <span className="font-mono">{month.month}</span>
          </span>
        </td>
        <td className="px-3 py-2 text-right font-mono t-primary">
          {formatCents(month.projectedCents)}
        </td>
        <td className="px-3 py-2 text-right font-mono t-secondary">
          {month.commissionCents !== 0 ? formatCents(month.commissionCents) : '—'}
        </td>
        <td className="px-3 py-2 text-right font-mono text-emerald-700 dark:text-emerald-300">
          {formatCents(month.netCents)}
        </td>
        <td className="px-3 py-2 text-right font-mono t-secondary">
          {month.emittedCents !== 0 ? formatCents(month.emittedCents) : '—'}
        </td>
        <td className="px-3 py-2 text-right font-mono t-secondary">
          {month.collectedCents !== 0 ? formatCents(month.collectedCents) : '—'}
        </td>
      </tr>
      {expanded &&
        month.items.map((item, idx) => (
          <tr
            key={`${month.month}-${idx}`}
            className="border-t border-base/20"
            style={{ background: 'var(--bg-input)' }}
          >
            <td className="px-3 py-1.5 pl-8 text-xs" colSpan={1}>
              <span className="inline-flex items-center gap-1.5 flex-wrap">
                <span
                  className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${
                    item.source === 'MILESTONE'
                      ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30'
                      : 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30'
                  }`}
                >
                  {item.source === 'MILESTONE'
                    ? t('forecast.sourceMilestone')
                    : t('forecast.sourceRecurring')}
                </span>
                <span className="t-primary">{item.label}</span>
                <span className="t-faint">· {item.clientName}</span>
                {item.opportunityTitle && <span className="t-faint">· {item.opportunityTitle}</span>}
              </span>
            </td>
            <td className="px-3 py-1.5 text-right font-mono t-secondary text-xs">
              {formatCents(item.baseCents)}
            </td>
            <td className="px-3 py-1.5 text-right font-mono t-secondary text-xs">
              {item.commissionCents !== 0 ? formatCents(item.commissionCents) : '—'}
            </td>
            <td className="px-3 py-1.5" colSpan={3}></td>
          </tr>
        ))}
    </>
  )
}
